import { SubmitClaim } from 'commonAssets/components/Forms/SubmitClaim';
import { ErrorHandler } from 'commonAssets/utils/ErrorHandler';
import { createElement, modal } from 'commonAssets/js/helpers';
import { FormFields } from "commonAssets/components/FormBase/FormFields";
import { outputDiv } from 'commonAssets/js/constants';

/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality. 
 */

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    console.log('[Initiate claim]...');
    const noticeIdField = document.querySelector('input[name="notice_id"]');
    const claimFields = document.querySelector('#claim-fields');
    const form = document.querySelector('#form');

    // handle form input
    form.addEventListener('input', handleFormInput);

    // notice id should be alphanumeric
    noticeIdField.pattern = '^[a-zA-Z0-9]+$';

    // prefill notice id if it exists
    if (claim.data?.zero_zero_id != null) {
        noticeIdField.value = claim.data.zero_zero_id;
        claimFields.style.display = 'none';
    }

    return claim;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    console.log('[Load class member]...');
    // custom code goes here
    // don't prepopulate name fields
    if (!claim.data.claim_id) {
        document.getElementById('first_name').value = '';
        document.getElementById('last_name').value = '';
    }
    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    console.log('[Process claim]...');
    const { transactions } = claim.inputs;
    const rows = getTransactionRows(transactions);
    const invalidDateRowIndex = rows.findIndex(isInvalidDateRange);
    const duplicatePhoneNoRowIndex = rows.findIndex(isDuplicatePhoneNumber);

    if (invalidDateRowIndex !== -1) {
        modal('Invalid Date Range', `Please ensure that the "From Date" is before the "To Date" on row ${invalidDateRowIndex + 1}.`);
        claim.flags.valid = false;
        return claim;
    }

    if (duplicatePhoneNoRowIndex !== -1) {
        modal('Duplicate Phone Number', 'Please ensure that each telephone number is only entered once.');
        claim.flags.valid = false;
        return claim;
    }

    return claim;
}

/**
 * Tracks the claim form.
 *
 * @param {Object} params - The parameters for tracking the claim form.
 * @param {Object} params.gtag - The gtag object.
 * @param {Object} params.fbq - The fbq object.
 */
export function trackClaimForm({ gtag, fbq = window.fbq }) {
    if (window.location.pathname !== '/submit-claim') {
        return;
    }

    // Track the page view
    gtag('event', 'conversion', { 'send_to': 'AW-10839088199/S-HVCOTLw5ADEMe4vbAo' });

    // Create a MutationObserver to watch for the form
    const formObserver = new MutationObserver((mutations, obs) => {
        const form = mutations.reduce((acc, mutation) => {
            if (mutation.type === 'childList' && mutation.addedNodes.item(0)?.id === 'form') {
                acc = mutation.addedNodes.item(0);
            }
            return acc;
        }, null);

        if (form) {
            console.log('[Track claim form]');
            obs.disconnect();

            // Track the form submission
            form.addEventListener('submit', () => {
                fbq('track', 'Lead');
                gtag("event", "generate_lead", {
                    currency: "USD",
                    value: 1
                });
            });
        }
    });

    // Start observing the document with the configured parameters
    formObserver.observe(outputDiv, {
        childList: true,
        subtree: true
    });
}

/**
 * Gets the transaction rows from the transactions.
 *
 * @param {Array<HTMLInputElement>} transactions - The transactions to get the rows from.
 * @returns {Array<Array<HTMLInputElement>>} The transaction rows.
 */
function getTransactionRows(transactions) {
    return Object.values(transactions.reduce((acc, input) => {
        const rowId = input.dataset.rowId;
        if (!acc[rowId]) {
            acc[rowId] = [];
        }
        acc[rowId].push(input);
        return acc;
    }, {}));
}

/**
 * Checks if a transaction row has a duplicate telephone number.
 *
 * @param {Array<HTMLInputElement>} row - The row to validate.
 * @param {number} index - The index of the row.
 * @param {Array<Array<HTMLInputElement>>} rows - The rows to validate.
 * @returns {boolean} - Whether the row has a duplicate telephone number.
 */
function isDuplicatePhoneNumber(row, rowIndex, rows) {
    const phoneNo = row.find(input => input.name.startsWith('telephone_number'));
    return rows.some((row, index) => index !== rowIndex && row.find(input => input.name.startsWith('telephone_number')).value === phoneNo.value);
}

/**
 * Checks if a transaction row is valid.
 *
 * @param {Array<HTMLInputElement>} row - The row to validate.
 * @returns {boolean} - Whether the row is valid.
 */
function isInvalidDateRange(row) {
    const fromDate = row.find(input => input.name.startsWith('from_date'));
    const toDate = row.find(input => input.name.startsWith('to_date'));
    return fromDate.value > toDate.value;
}

/**
 * Handles the form input.
 *
 * @param {Event} event - The event to handle.
 */
function handleFormInput(event) {
    if (event.target.type === 'tel') {
        // remove all non-numeric characters
        event.target.value = event.target.value.replace(/[^0-9-]/g, '');
        // format the value as (123) 456-7890
        event.target.value = event.target.value.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
}

export class CustomSubmitClaim extends SubmitClaim {
    /**
     * Creates an instance of SubmitClaim.
     * 
     * @param {Object} params - The parameters for creating the instance.
     * @param {string} params.appClaimForm - The path to the app claim form.
     * @param {string} params.userLanguage - The user's language.
     * @param {Function?} params.customInitClaim - Optional custom init claim function.
     * @param {Function?} params.customProcessClaim - Optional custom process claim function.
     * @param {Function?} params.customLoadMember - Optional custom load member function.
     * @param {Function?} params.customDstProcess - Optional custom dst process function.
     * @param {ErrorHandler?} params.errorHandler - Optional error handler.
     * @returns {SubmitClaim} - The created instance.
     */
    constructor({ appClaimForm, userLanguage, customInitClaim = null, customProcessClaim = null, customLoadMember = null, customDstProcess = null, errorHandler = new ErrorHandler() }) {
        super(appClaimForm, userLanguage, customInitClaim, customProcessClaim, customLoadMember, customDstProcess, errorHandler);
    }

    /**
     * Adds a new transaction row to the given table.
     * @param {HTMLTableElement} table - The table to add the row to.
     * @param {HTMLTableSectionElement} tableTBODY - The table body to add the row to.
     * @param {string} tableID - The ID of the table.
     * @param {number} countRow - The count of the row.
     * @returns {Object} An object containing the table body and the fields row.
     */
    claim_transaction_row(table, tableTBODY, tableID, countRow) {
        const fieldsRow = createElement("tr", {
            class: "transaction-row", // ada compliance updates - added
            id: "transaction_row_" + tableID + "_" + countRow, // ada compliance updates - added
        });

        tableTBODY.appendChild(fieldsRow);

        let fieldID = 0;
        table.fields.forEach(field => {
            fieldID++;
            const td = createElement("td", { class: "transaction " + field.name });
            const id = field.name + "__" + countRow;
            const div = createElement("div", {}, [
                createElement("label", {
                    for: id,
                }, [], `${field.label}${field.required ? "*" : ""} <span class="error"></span>`),
            ]);

            const input = FormFields(field, "", id, div, field.validation, fieldsRow);

            // set unique name for each field
            input.name = id;
            input.id = id;

            // set item-id attribute
            input.setAttribute("item-id", countRow);
            input.setAttribute("data-row-id", countRow);

            // set min and max date for date fields
            if (input.type === "date") {
                input.min = new Date('2015-03-05').toISOString().split('T')[0];
                input.max = new Date('2025-01-02').toISOString().split('T')[0];
            }

            // set max length for tel fields
            if (input.type === "tel") {
                input.pattern = '^[0-9]{3}-[0-9]{3}-[0-9]{4}$';
                input.maxLength = 12;
            }

            div.appendChild(input);
            td.appendChild(div);
            fieldsRow.appendChild(td);
        });

        return { tableTBODY, fieldsRow };
    }
}